import { useState, useCallback, useRef, useEffect } from "react";
import { v4 as uuidv4 } from "uuid";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Box, IconButton, Typography } from "@mui/material";
import { Minus, MessageCircle } from "lucide-mui";
import { useTheme } from "../context/ThemeContext";
import { useSSE } from "../context/SSEContext";
import { useNotification } from "../context/NotificationContext";
import { useDevLogger } from "../context/DevLogContext";
import { useAuth } from "../context/AuthContext";
// import { githubWorkflowService } from "../api/githubWorkflowService";
import { useSidebar } from "../context/SidebarContext";
import {
  checkDuplicateFiles,
  sendUploadFiles,
  VALID_FILE_EXTENSIONS,
} from "../api/filesApi";
import ChatComponent from "./ChatComponent";
import DuplicateFileDialog from "./DuplicateFileDialog";
import { ChatFileDropOverlay } from "../components";

export const PLATFORM_SHARED_SESSION_ID = "platform-shared-session";
const MIN_CHAT_WIDTH = 300;
const MAX_CHAT_WIDTH = 600;
const DEFAULT_CHAT_WIDTH = 300;

export default function ChatLayout() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { registerCallback } = useSSE();
  const { addNotification } = useNotification();
  const { addDevLog } = useDevLogger();
  const { user, updateUser, permissions } = useAuth();
  const { sidebarOpen: isMainSidebarOpen, openSidebar: openMainSidebar } =
    useSidebar();
  const location = useLocation();
  const isModelRoute = location.pathname.startsWith("/model");
  const tabType = isModelRoute ? "toc-editor" : "data-manager";
  const fileId = isModelRoute
    ? new URLSearchParams(location.search).get("file_id")
    : null;

  const [messages, setMessages] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [refetchCount, setRefetchCount] = useState(0);

  const incrementRefetch = useCallback(() => setRefetchCount(c => c + 1), []);
  const [agentCurrentStep, setAgentCurrentStep] = useState(null);
  const [viewedStep, setViewedStep] = useState(null);
  // null | "active" | "paused" | "complete"
  const [logbookStatus, setLogbookStatus] = useState(null);

  // ── SSE: snapshot restore complete ─────────────────────────────────────────
  useEffect(() => {
    const cleanup = registerCallback("restore_complete", data => {
      const message = data.message || {};
      const snapshotUserId = data.snapshot_user_id ?? message.snapshot_user_id;
      if (message.success || data.success) {
        if (snapshotUserId != null) updateUser({ id: snapshotUserId });
        addNotification(
          `Snapshot "${data.snapshot_name || message.snapshot_name}" restored successfully!`,
          "success",
        );
        addDevLog(`Snapshot restored successfully!`, "success");
        setCurrentFolderId(null);
        if (location.pathname !== "/") navigate("/");
      } else {
        if (message.error_type === "schema_validation") {
          addNotification("Schema Validation Failed!", "error");
          addDevLog(
            `Schema Validation Failed!\n${message.full_error ?? ""}`,
            "error",
          );
        } else {
          addNotification(`Restore failed: ${message.message}`, "error");
          addDevLog(`Restore failed: ${message.message}`, "error");
        }
      }
    });
    return cleanup;
  }, [registerCallback]);

  // ── SSE: datastore reset complete ───────────────────────────────────────────
  useEffect(() => {
    const cleanup = registerCallback("reset_complete", async data => {
      const message = data.message || {};
      if (message.success) {
        addNotification(
          "Datastore reset successfully! File list cleared.",
          "success",
        );
        addDevLog("Datastore reset successfully!", "success");
        // try {
        //   await githubWorkflowService.restartDeployments();
        //   addNotification(
        //     "Services restart workflow triggered successfully!",
        //     "success",
        //   );
        //   addDevLog(
        //     "GitHub workflow triggered. Services will restart shortly.",
        //     "success",
        //   );
        // } catch (err) {
        //   addNotification(
        //     `Failed to trigger services restart: ${err.message}`,
        //     "error",
        //   );
        //   addDevLog(
        //     `Failed to trigger GitHub workflow: ${err.message}`,
        //     "error",
        //   );
        // }
        setCurrentFolderId(null);
        if (location.pathname !== "/") navigate("/");
      } else {
        addNotification(`Reset failed: ${message.message}`, "error");
      }
    });
    return cleanup;
  }, [registerCallback]);

  // ── SSE: schema validation warning on restore ───────────────────────────────
  useEffect(() => {
    const cleanup = registerCallback("validation_warning", data => {
      const message = data.message || {};
      addDevLog(
        `Schema Validation Warning\n\n${message.message}\n\nRestore completed but schema validation could not be performed. Data may not be fully compatible.`,
        "warn",
      );
    });
    return cleanup;
  }, [registerCallback]);

  // Navigates to TocEditComponent for both view and edit actions
  const handleTocAction = useCallback(
    (file_id, file_name, action, dpm_uuid = null) => {
      const params = new URLSearchParams({
        file_id: String(file_id),
        file_name: String(file_name),
        ...(dpm_uuid ? { dpm_uuid: String(dpm_uuid) } : {}),
      });
      navigate(`/model?${params.toString()}`);
    },
    [navigate],
  );

  const [chatWidth, setChatWidth] = useState(DEFAULT_CHAT_WIDTH);
  const [isResizing, setIsResizing] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeSection, setActiveSection] = useState("overview");
  const [attachedFilesInChat, setAttachedFilesInChat] = useState([]);
  const [isFileDragOverChat, setIsFileDragOverChat] = useState(false);
  const chatPanelRef = useRef(null);
  const chatOpenInitialized = useRef(false);

  useEffect(() => {
    if (chatOpenInitialized.current || permissions === null) return;
    chatOpenInitialized.current = true;
    setIsChatOpen(!permissions.toc_chat_minimized);
  }, [permissions]);

  const updateMessages = useCallback(
    (sessionId, newMessages, overRide = false) => {
      setMessages(prev => {
        const resolved =
          typeof newMessages === "function" ? newMessages(prev) : newMessages;
        return overRide ? resolved : [...prev, ...resolved];
      });
    },
    [],
  );

  const updateMessageStatus = useCallback(
    (sessionId, reqId, status, message, file_id, progress) => {
      setMessages(prev =>
        prev.map(m =>
          m.type === "FILE_UPLOAD" && m.value?.request_id === reqId
            ? {
                ...m,
                value: {
                  ...m.value,
                  status,
                  message,
                  ...(file_id != null && { file_id }),
                  ...(progress != null && { progress }),
                },
              }
            : m,
        ),
      );
    },
    [],
  );

  const updateSkeletonStatus = useCallback(
    (
      newStatus,
      session_id = null,
      req_id = null,
      prevStatus = null,
      skeletonData = null,
    ) => {
      setMessages(prev => {
        const mergeValue = value => ({
          ...value,
          status: newStatus,
          ...(skeletonData != null && {
            data: { ...value?.data, plan: skeletonData },
          }),
        });
        if (req_id) {
          return prev.map(msg => {
            if (
              msg.type === "AGENT_RESPONSE" &&
              msg.value?.topic === "SKELETON_GENERATED" &&
              msg.value?.request_id === req_id
            ) {
              return { ...msg, value: mergeValue(msg.value) };
            }
            return msg;
          });
        } else {
          const messages = [...prev];
          for (let i = messages.length - 1; i >= 0; i--) {
            const msg = messages[i];
            if (
              msg.type === "AGENT_RESPONSE" &&
              msg.value?.topic === "SKELETON_GENERATED" &&
              (!prevStatus || msg.value?.status === prevStatus)
            ) {
              messages[i] = { ...msg, value: mergeValue(msg.value) };
              break;
            }
          }
          return messages;
        }
      });
    },
    [],
  );

  // ── Drag file into chat to attach ───────────────────────────────────────────
  const onAttachFileToChat = useCallback(fileMeta => {
    setAttachedFilesInChat(prev =>
      prev.some(f => f.file_id === fileMeta.file_id)
        ? prev // already attached
        : [...prev, fileMeta],
    );
  }, []);

  const onRemoveAttachedFile = useCallback(fileId => {
    setAttachedFilesInChat(prev => prev.filter(f => f.file_id !== fileId));
  }, []);

  const onClearAttachedFiles = useCallback(() => {
    setAttachedFilesInChat([]);
  }, []);

  // ── File upload ──────────────────────────────────────────────────────────────
  const fileInputRef = useRef(null);

  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateFiles, setDuplicateFiles] = useState([]);
  const [orgConflictDialogOpen, setOrgConflictDialogOpen] = useState(false);
  const [orgConflictFiles, setOrgConflictFiles] = useState([]);
  const [orgConflictProposedNames, setOrgConflictProposedNames] = useState({});
  const pendingOrgConflictsRef = useRef({ files: [], proposedNames: {} });
  const orgConflictFilesRef = useRef([]);
  const orgConflictProposedNamesRef = useRef({});

  const setOrgConflictData = (files, proposedNames) => {
    orgConflictFilesRef.current = files;
    orgConflictProposedNamesRef.current = proposedNames;
    setOrgConflictFiles(files);
    setOrgConflictProposedNames(proposedNames);
  };

  const showPendingOrgConflicts = () => {
    const { files, proposedNames } = pendingOrgConflictsRef.current;
    pendingOrgConflictsRef.current = { files: [], proposedNames: {} };
    if (files.length > 0) {
      setOrgConflictData(files, proposedNames);
      setOrgConflictDialogOpen(true);
    }
  };

  const uploadFiles = async (targetFiles, renamedMap = null) => {
    if (!user?.id || !targetFiles.length) return;
    const uploadMessages = targetFiles.map(file => ({
      type: "FILE_UPLOAD",
      value: {
        file_name: renamedMap?.[file.name] || file.name,
        file_id: null,
        status: "UPLOADING",
        request_id: uuidv4(),
      },
    }));
    updateMessages(PLATFORM_SHARED_SESSION_ID, uploadMessages);
    try {
      const fileEntries = uploadMessages.map((m, i) => ({
        name: m.value.file_name,
        file: targetFiles[i],
        request_id: m.value.request_id,
      }));
      const { failedEntries } = await sendUploadFiles(
        user.id,
        PLATFORM_SHARED_SESSION_ID,
        fileEntries,
        {
          onFileUploaded: f => {
            addDevLog(
              `File "${f.file_name}" uploaded. Ingestion in progress...`,
              "success",
            );
          },
        },
      );
      failedEntries.forEach(({ entry, error }) => {
        updateMessageStatus(
          PLATFORM_SHARED_SESSION_ID,
          entry.request_id,
          "INGESTION_FAILED",
          null,
        );
        addDevLog(`File "${entry.name}" upload failed: ${error}`, "error");
        addNotification(`"${entry.name}" failed to upload`, "error");
      });
    } catch (e) {
      uploadMessages.forEach(m =>
        updateMessageStatus(
          PLATFORM_SHARED_SESSION_ID,
          m.value.request_id,
          "INGESTION_FAILED",
          null,
        ),
      );
      addNotification("File upload failed: " + e.message, "error");
    }
  };

  const handleUploadFiles = async targetFiles => {
    try {
      const {
        existingFileNames,
        orgDuplicateFileNames,
        orgDuplicateProposedNames,
      } = await checkDuplicateFiles(
        user.id,
        PLATFORM_SHARED_SESSION_ID,
        targetFiles.map(f => f.name),
      );

      const userDuplicates = targetFiles.filter(f =>
        existingFileNames.includes(f.name),
      );
      const orgConflicts = targetFiles.filter(
        f =>
          !existingFileNames.includes(f.name) &&
          orgDuplicateFileNames.includes(f.name),
      );
      const uniqueFiles = targetFiles.filter(
        f =>
          !existingFileNames.includes(f.name) &&
          !orgDuplicateFileNames.includes(f.name),
      );

      if (uniqueFiles.length > 0) uploadFiles(uniqueFiles);

      if (userDuplicates.length > 0) {
        pendingOrgConflictsRef.current = {
          files: orgConflicts,
          proposedNames: orgDuplicateProposedNames,
        };
        setDuplicateFiles(userDuplicates);
        setDuplicateDialogOpen(true);
      } else if (orgConflicts.length > 0) {
        setOrgConflictData(orgConflicts, orgDuplicateProposedNames);
        setOrgConflictDialogOpen(true);
      }
    } catch {
      uploadFiles(targetFiles);
    }
  };

  const handleFileSelect = e => {
    const files = Array.from(e.target.files).filter(f =>
      VALID_FILE_EXTENSIONS.includes(
        "." + f.name.split(".").pop().toLowerCase(),
      ),
    );
    e.target.value = null;
    if (files.length > 0) handleUploadFiles(files);
  };

  const openFilePicker = () => fileInputRef.current?.click();

  // ── Resize ───────────────────────────────────────────────────────────────────
  const handleResizeStart = () => setIsResizing(true);

  useEffect(() => {
    if (!isResizing) return;
    const handleMouseMove = e => {
      const right = chatPanelRef.current?.getBoundingClientRect().right;
      if (!right) return;
      const newWidth = Math.min(
        MAX_CHAT_WIDTH,
        Math.max(MIN_CHAT_WIDTH, right - e.clientX),
      );
      setChatWidth(newWidth);
    };
    const handleMouseUp = () => setIsResizing(false);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isResizing]);

  useEffect(() => {
    if (location.pathname.startsWith("/model")) setIsChatOpen(false);
    else {
      setIsChatOpen(true);
      setLogbookStatus(null);
    }
  }, [location.pathname]);

  const outletContext = {
    chatSessionId: PLATFORM_SHARED_SESSION_ID,
    updateMessages,
    currentFolderId,
    setCurrentFolderId,
    refetchCount,
    incrementRefetch,
    handleTocAction,
    agentCurrentStep,
    setViewedStep,
    logbookStatus,
    setLogbookStatus,
    openFilePicker,
    onCloseChatPanel: () => setIsChatOpen(false),
    onOpenChatPanel: () => setIsChatOpen(true),
    isSidebarOpen,
    setIsSidebarOpen,
    activeSection,
    setActiveSection,
    onAttachFileToChat,
    getChatPanelRect: () =>
      chatPanelRef.current?.getBoundingClientRect() ?? null,
    onFileDragOverChatChange: over => setIsFileDragOverChat(over),
  };

  return (
    <Box
      sx={{
        display: "flex",
        height: "100%",
        width: "100%",
        overflow: "hidden",
        position: "relative",
        userSelect: isResizing ? "none" : "auto",
      }}
    >
      {/* Page content — position:relative so the modal overlay portal fills this area */}
      <Box
        sx={{ flex: 1, overflow: "hidden", minWidth: 0, position: "relative" }}
      >
        <Outlet context={outletContext} />
        <div
          id='tab-content-overlay-allfiles-chat'
          style={{ pointerEvents: "all" }}
        />
      </Box>

      {/* Chat panel — always mounted so messages persist; width animates to 0 when closed */}
      <Box
        ref={chatPanelRef}
        sx={{
          width: isChatOpen ? `${chatWidth}px` : "0px",
          opacity: isChatOpen ? 1 : 0,
          flexShrink: 0,
          borderLeft: isChatOpen ? "1px solid" : "none",
          borderColor: isDark ? "#6A707966" : "#D5D9DF",
          backgroundColor: isDark ? "#171819" : "#F3F6FA",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          position: "relative",
          transition: isResizing
            ? "none"
            : "width 0.32s cubic-bezier(0.4,0,0.2,1), opacity 0.22s ease",
        }}
      >
        {/* Resize handle */}
        <Box
          onMouseDown={handleResizeStart}
          title='Resize panel'
          sx={{
            position: "absolute",
            left: "-2px",
            top: 0,
            height: "100%",
            width: "4px",
            cursor: "col-resize",
            zIndex: 50,
            "&:hover": {
              backgroundColor: isDark ? "#58A6FF40" : "#58A6FF30",
            },
          }}
        />

        {/* Header with collapse button */}
        <Box
          sx={{
            height: "45px",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            px: "12px",
          }}
        >
          <IconButton
            onClick={() => setIsChatOpen(false)}
            size='small'
            title='Collapse chat'
            sx={{
              width: "36px",
              height: "32px",
              borderRadius: "8px",
              color: isDark ? "#FFFFFF" : "#434950",
              background: isDark
                ? "linear-gradient(135deg, rgba(67, 73, 80, 0.8) 0%, rgba(49, 54, 59, 0.8) 50%, rgba(49, 54, 59, 0.8) 100%)"
                : "#D3DAE1",
              border: "1px solid",
              borderColor: isDark ? "#6A70791A" : "#D5D9DF",
            }}
          >
            <Minus sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        {/* Portal target for AnimationPropertiesComponent overlay (TocEditComponent at /model) */}
        <div
          id='chat-content-overlay-model-creator-session'
          style={{ pointerEvents: "all" }}
        />
        <ChatComponent
          messages={messages}
          updateMessages={updateMessages}
          updateMessageStatus={updateMessageStatus}
          updateSkeletonStatus={updateSkeletonStatus}
          sessionId={PLATFORM_SHARED_SESSION_ID}
          currentFolderId={currentFolderId}
          onRefetch={incrementRefetch}
          handleTocAction={handleTocAction}
          currentAnchorId='allfiles-chat'
          tabType={tabType}
          fileId={fileId}
          onViewModel={filename =>
            navigate(`/model?file_name=${encodeURIComponent(filename)}`)
          }
          onStepChange={setAgentCurrentStep}
          currentStep={viewedStep}
          attachedFilesInChat={attachedFilesInChat}
          onRemoveAttachedFile={onRemoveAttachedFile}
          onClearAttachedFiles={onClearAttachedFiles}
          isFileDragOver={isFileDragOverChat}
          onToggleSidebar={
            isModelRoute
              ? !isSidebarOpen
                ? () => setIsSidebarOpen(true)
                : undefined
              : !isMainSidebarOpen
                ? openMainSidebar
                : undefined
          }
        />

        <ChatFileDropOverlay visible={isFileDragOverChat} />
      </Box>

      {/* Floating re-open button — draggable */}
      {!isChatOpen && (
        <Box
          onClick={() => setIsChatOpen(true)}
          sx={{
            position: "fixed",
            bottom:
              isModelRoute && activeSection === "steps"
                ? "184px"
                : isModelRoute
                  ? "90px"
                  : "42px",
            right: isModelRoute ? "24px" : "44px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            p: "16px",
            borderRadius: "12px",
            background: isDark
              ? "linear-gradient(180deg, rgba(67, 73, 80, 0.2) 0%, rgba(243, 246, 250, 0.02) 99.52%),linear-gradient(0deg, #28292A, #28292A)"
              : "#FFFFFF",
            boxShadow: isDark
              ? "0px 2px 5px 0px #F3F6FA26 inset"
              : "0px 1px 12px 0px #E3E3E399",
            cursor: "pointer",
            userSelect: "none",
            zIndex: 100,
          }}
        >
          <MessageCircle
            sx={{ fontSize: 18, color: isDark ? "#A8AEB6" : "#434950" }}
          />
          <Typography
            sx={{
              fontFamily: "Inter",
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "20px",
              color: isDark ? "#ECEFF3" : "#434950",
              userSelect: "none",
            }}
          >
            {isModelRoute && logbookStatus === "active"
              ? "Need Help"
              : (permissions?.chat_bubble_text ?? "Build or Search")}
          </Typography>
        </Box>
      )}

      <input
        ref={fileInputRef}
        type='file'
        hidden
        multiple
        accept='.step,.pdf,.jpg,.jpeg,.png,.svg,.dpm'
        onChange={handleFileSelect}
      />
      <DuplicateFileDialog
        open={duplicateDialogOpen}
        duplicateFiles={duplicateFiles.map(f => f.name)}
        onReplaceAll={() => {
          uploadFiles(duplicateFiles);
          setDuplicateDialogOpen(false);
          setDuplicateFiles([]);
          showPendingOrgConflicts();
        }}
        onSkip={() => {
          setDuplicateDialogOpen(false);
          setDuplicateFiles([]);
          showPendingOrgConflicts();
        }}
        onCancel={() => {
          setDuplicateDialogOpen(false);
          setDuplicateFiles([]);
          showPendingOrgConflicts();
        }}
      />
      <DuplicateFileDialog
        open={orgConflictDialogOpen}
        mode='org_conflict'
        duplicateFiles={orgConflictFiles.map(f => f.name)}
        onReplaceAll={() => {
          uploadFiles(
            orgConflictFilesRef.current,
            orgConflictProposedNamesRef.current,
          );
          setOrgConflictDialogOpen(false);
          setOrgConflictData([], {});
        }}
        onSkip={() => {
          setOrgConflictDialogOpen(false);
          setOrgConflictData([], {});
        }}
        onCancel={() => {
          setOrgConflictDialogOpen(false);
          setOrgConflictData([], {});
        }}
      />
    </Box>
  );
}
